import { useMemo, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Select } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import {
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { stats, type BulkAction, type Incident, type StatusFilter } from '../../api/stats';
import { formatDateTime } from '../../lib/format';
import { useHubLogin } from '../../session';
import { EntryStatusChip, OrgLink, RangeTabs, rangeWindow, statsError, useParam, useRange } from './shared';
import './statistics.css';

const STATUS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Open + acknowledged' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

const ACTIONS: { action: BulkAction; label: string; done: string }[] = [
  { action: 'ack', label: 'Acknowledge', done: 'acknowledged' },
  { action: 'close', label: 'Close', done: 'closed' },
];

/** Selection + bulk acknowledge/close (closed ids, `h-…`, cannot change). */
export function useBulk(
  apply: (ids: string[], action: BulkAction) => Promise<{ changed: number }>,
  after: () => void
) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const toast = useNotice();
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const run = async (ids: string[], action: BulkAction, done: string) => {
    setBusy(true);
    try {
      const r = await apply(ids, action);
      toast.success(`${r.changed} ${done}`);
      setSelected(new Set());
      after();
    } catch (e) {
      toast.error(statsError(e));
    } finally {
      setBusy(false);
    }
  };
  return { selected, setSelected, toggle, run, busy, toast };
}

/** Incidents (performance today; silent / regression / error-spike later), newest first, with bulk actions. */
export function IncidentsTable({ customerId, compact }: { customerId?: number; compact?: boolean }) {
  const login = useHubLogin();
  const canAdmin = hasScope(login?.scopes, SCOPES.statsAdmin);
  const [status, setStatus] = useParam<StatusFilter>('status', 'active');
  const [range, setRange] = useRange('30d');
  const w = useMemo(() => rangeWindow(range), [range]);
  const list = useAsync(
    () => stats.incidents({ status, customerId, from: w.from, to: w.to, limit: 500 }),
    [status, customerId, w.from, w.to]
  );
  const bulk = useBulk(
    (ids, action) => stats.incidentBulk(ids, action),
    () => void list.reload()
  );
  const items = list.data?.items ?? [];
  const selectable = items.filter((i) => i.status !== 'closed');
  const allOn = selectable.length > 0 && selectable.every((i) => bulk.selected.has(i.id));
  const ids = [...bulk.selected];
  return (
    <section className="sx-body" data-testid="incidents">
      {bulk.toast.element}
      <div className="sx-toolbar">
        <RangeTabs value={range} onChange={setRange} />
        <Select
          aria-label="Status"
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
          options={STATUS}
        />
      </div>
      <ErrorBox error={list.error ? statsError(list.error) : null} />
      <Loading loading={list.loading && !list.data} />
      {canAdmin && ids.length > 0 && (
        <div className="sx-bulkbar" data-testid="bulkbar">
          <span>{ids.length} selected</span>
          {ACTIONS.map((a) => (
            <Button
              key={a.action}
              size="sm"
              variant="secondary"
              loading={bulk.busy}
              onClick={() => void bulk.run(ids, a.action, a.done)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="incidents-table">
            <thead>
              <tr>
                {canAdmin && (
                  <th>
                    <input
                      type="checkbox"
                      className="sx-check"
                      aria-label="Select all"
                      checked={allOn}
                      onChange={() =>
                        bulk.setSelected(allOn ? new Set() : new Set(selectable.map((i) => i.id)))
                      }
                    />
                  </th>
                )}
                <th>Incident</th>
                {!compact && <th>Organization</th>}
                <th>Severity</th>
                <th>Started</th>
                <th>Methods</th>
                <th>Status</th>
                {canAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map((i: Incident) => (
                <tr key={i.id} data-incident={i.id}>
                  {canAdmin && (
                    <td>
                      {i.status !== 'closed' && (
                        <input
                          type="checkbox"
                          className="sx-check"
                          aria-label={`Select incident ${i.id}`}
                          checked={bulk.selected.has(i.id)}
                          onChange={() => bulk.toggle(i.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="nyk-card-title sx-msg">
                    {i.summary || `${i.kind} incident`}
                    <div className="nyk-ui-hint">
                      {i.kind}
                      {i.checkType && ` · ${i.checkType}`}
                      {i.outlierCount != null && ` · ${i.outlierCount} outliers`}
                    </div>
                  </td>
                  {!compact && (
                    <td data-label="Organization">
                      <OrgLink customerId={i.customerId} name={i.name} />
                    </td>
                  )}
                  <td data-label="Severity">{i.severity ?? '—'}</td>
                  <td data-label="Started">{formatDateTime(i.startedAt)}</td>
                  <td data-label="Methods" className="nyk-wrap">
                    {i.methods.join(', ') || '—'}
                  </td>
                  <td data-label="Status">
                    <EntryStatusChip status={i.status} />
                    {(i.acknowledgedBy || i.closedBy) && (
                      <div className="nyk-ui-hint">by {i.closedBy ?? i.acknowledgedBy}</div>
                    )}
                  </td>
                  {canAdmin && (
                    <td className="nyk-row-actions">
                      {i.status === 'open' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void bulk.run([i.id], 'ack', 'acknowledged')}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {i.status !== 'closed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void bulk.run([i.id], 'close', 'closed')}
                        >
                          Close
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {list.data && !items.length && <EmptyState title="No incidents match." />}
        {list.data?.hasMore && <p className="nyk-ui-hint">Showing the newest 500; narrow the range.</p>}
      </div>
    </section>
  );
}

export function IncidentsPage() {
  const [customer] = useParam('customerId', '');
  return (
    <Page
      title="Incidents"
      description="Performance incidents from the nightly check. Closing moves an incident to history (its id changes)."
    >
      <IncidentsTable customerId={customer ? Number(customer) : undefined} />
    </Page>
  );
}
